import { gql, useMutation } from '@apollo/client';
import { useState, useEffect } from 'react'
import { Flex, Box, Text, Heading, Card, Button } from 'rebass/styled-components'
import * as Yup from 'yup';
import { IOkPayload } from '../graphql/types/mutations/generics';
import styled from 'styled-components';
import Header from '../components/Header';
import FullLayout2 from '../components/FullLayout';
import { useMe } from '../hooks/useMe';
import PleaseWait from '../components/PleaseWait';
import { CURRENT_USER } from '../graphql/queries';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { onError } from '@apollo/client/link/error';
import { Form, Formik } from 'formik';
import FormField from '../components/FormField';
import TextInput from '../components/TextInput';

const schema = Yup.object().shape({
  oldPassword: Yup.string()
    .trim()
    .required('Required'),
  newPassword: Yup.string()
    .trim()
    .required('Required')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*\.])(?=.{8,24})/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    ),
  passwordConfirm: Yup.string()
    .trim()
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
    .required('Required'),
});

const Container = styled(Box)`
  max-width: 1024px;
  width: 100%;
  margin: 0 auto !important;
`;

const CardContainer = styled(Flex)`
  max-width: 400px;
`;

const CHANGE_PASSWORD = gql`
  mutation healthCoachChangePassword($oldPassword: String!, $newPassword: String!) {
    healthCoachChangePassword(oldPassword: $oldPassword, newPassword: $newPassword) {
      ok
    }
  }
`;

interface IChangePasswordInput {
  oldPassword: string,
  newPassword: string
}

interface IChangePasswordOutput {
  healthCoachChangePassword: IOkPayload
}

interface IPasswordChangeProps {

}

export const PasswordChange: React.FC<IPasswordChangeProps> = ({ }) => {
  const navigate = useNavigate()
  const { session, loadingMe, errorMe } = useMe()
  const [change, { loading }] = useMutation<IChangePasswordOutput, IChangePasswordInput>(CHANGE_PASSWORD, {
    refetchQueries: [{ query: CURRENT_USER }],
    awaitRefetchQueries: true,
    onCompleted: e => {
      if (e && e.healthCoachChangePassword && e.healthCoachChangePassword.ok) {
        toast.success('Password Changed', {
          autoClose: 1500
        })
        // navigate('/')
      } else {
        toast.error('An error occured', {
          autoClose: 1500
        })
      }
    },
    onError: (e) => {
      e.graphQLErrors.forEach((error) => {
        toast.error(error.message, {
          autoClose: 3000,
        });
      });
    }
  })

  if (loadingMe || loading) {
    return <PleaseWait />
  }
  if (!session) {
    return null
  }
  const handleSubmit = async (values: {
    oldPassword: string;
    newPassword: string;
    passwordConfirm: string;
  }) => {
    const { newPassword, oldPassword, passwordConfirm } = values;
    if (newPassword !== passwordConfirm) {
      toast.error("Passwords don't match", { autoClose: 1500 });
    }
    try {
      await change({ variables: { oldPassword, newPassword } });
    } catch (error) {
    }
  };
  return (
    <FullLayout2 style={{ overflow: 'auto', zIndex: 10 }}>
      <Container>
        <Flex flex={1} flexDirection="column">
          <Header dark={false} childContainerStyle={{ marginTop: '-16px' }}>
            <Heading sx={{ marginLeft: '97px' }}>Health Coaches</Heading>
          </Header>
        </Flex>
        <Flex flex={1} alignItems="center" justifyContent="center" mb={5}>
          <CardContainer flex={1} px={3}>
            <Card p={4}>
              <Flex flexDirection="column">
                <Box mb={4}>
                  <Heading textAlign="center" fontWeight={500} mb={3}>
                    Password Expiration
                  </Heading>

                  <Text>{session.currentUserHealthCoach.passwordExpired ? 'Your password is expired. Passwords are expiring after 90 days' : `Your password will expire in ${session.currentUserHealthCoach.daysBeforePasswordExpires} days`}</Text>
                </Box>
                <Formik
                  initialValues={{
                    oldPassword: '',
                    newPassword: '',
                    passwordConfirm: '',
                  }}
                  validationSchema={schema}
                  onSubmit={(values) => handleSubmit(values)}
                  render={({ touched, errors, isValid }) => (
                    <Form>
                      <FormField mb={3} error={touched.oldPassword && errors.oldPassword}>
                        <TextInput placeholder="Current Password" type="password" name="oldPassword" />
                      </FormField>

                      <FormField mb={3} error={touched.newPassword && errors.newPassword}>
                        <TextInput placeholder="New Password" type="password" name="newPassword" />
                      </FormField>

                      <FormField mb={4} error={touched.passwordConfirm && errors.passwordConfirm}>
                        <TextInput placeholder="Re-enter New Password" type="password" name="passwordConfirm" />
                      </FormField>

                      <Flex justifyContent="center" flexDirection={'row'} alignItems={'center'}>
                        <Button disabled={!isValid || loading} variant="primary" type="submit">
                          Change Password
                        </Button>
                        {!session.currentUserHealthCoach.passwordExpired && (
                          <Button onClick={() => navigate('/')} ml={3} disabled={!isValid || loading} variant="outline" type="button">
                            Not Now
                          </Button>
                        )}
                      </Flex>
                    </Form>
                  )}
                />
              </Flex>
            </Card>
          </CardContainer>
        </Flex>
      </Container>
    </FullLayout2>);
}